import { useEffect, useState } from "react";
import DateField from "./DateField";
import AppModal from "./AppModal";
import { childrenService } from "../services/apiService";
import { usePermission } from "../hooks/usePermission";
import { useAuthStore } from "../store/authStore";
import { showToast } from "../utils/toast";
import { validateForm } from "../utils/validate";

const EMPTY = {
  id: null,
  family_id: "",
  centre_id: "",
  first_name: "",
  last_name: "",
  dob: "",
  room: "",
  program: "",
  days: "",
  enrolment_status: "Enrolled",
  is_active: 1,
};

const DAY_OPTIONS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

const normalizeDays = (days) =>
  (days || "")
    .split(",")
    .map((d) => d.trim())
    .filter((d) => DAY_OPTIONS.includes(d))
    .join(",");

export default function ChildFormModal({ open, onClose, family, child, onSaved }) {
  const { isGroupAdmin } = usePermission();
  const currentUser = useAuthStore((s) => s.user);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (child) {
      setForm({
        ...EMPTY,
        ...child,
        dob: child.dob ? String(child.dob).slice(0, 10) : "",
        days: normalizeDays(child.days),
        family_id: child.family_id || family?.id || "",
        centre_id: child.centre_id || family?.centre_id || "",
      });
    } else {
      setForm({
        ...EMPTY,
        family_id: family?.id || "",
        centre_id: family?.centre_id || currentUser?.centre_id || "",
      });
    }
  }, [open, child, family, currentUser?.centre_id]);

  if (!open) return null;

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
  };

  const selectedDays = normalizeDays(form.days)
    .split(",")
    .filter(Boolean);

  const toggleDay = (day) =>
    setForm((f) => {
      const current = (f.days || "")
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);
      const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
      return { ...f, days: DAY_OPTIONS.filter((d) => next.includes(d)).join(",") };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const centreId = isGroupAdmin
      ? form.centre_id
      : currentUser?.centre_id || form.centre_id;

    if (!centreId) {
      showToast("error", "Set the family centre on Personal details before adding a child");
      return;
    }

    if (
      !validateForm(e.currentTarget, [
        { name: "first_name", value: form.first_name, label: "First name", required: true },
        {
          name: "dob",
          value: form.dob,
          label: "Date of birth",
          required: true,
          custom: (v) =>
            v && new Date(v) > new Date() ? "Date of birth cannot be in the future" : null,
        },
      ])
    )
      return;

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        centre_id: centreId,
        days: normalizeDays(form.days),
      };
      const res = form.id
        ? await childrenService.update(payload)
        : await childrenService.create(payload);
      if (res?.success) {
        showToast("success", form.id ? "Child updated" : "Child created");
        onSaved?.();
        onClose();
      } else {
        showToast("error", res?.msg || "Save failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={form.id ? "Edit Child" : "Add Child"}
      size="lg"
      stacked
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" form="child-form-modal" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Saving…" : form.id ? "Save Changes" : "Add Child"}
          </button>
        </>
      }
    >
      <form id="child-form-modal" onSubmit={handleSubmit} noValidate>
        <div className="row g-2">
          <div className="col-md-6">
            <label className="form-label">First name <span className="text-danger">*</span></label>
            <input name="first_name" className="form-control" value={form.first_name} onChange={onChange} required />
          </div>
          <div className="col-md-6">
            <label className="form-label">Last name</label>
            <input name="last_name" className="form-control" value={form.last_name || ""} onChange={onChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Date of birth <span className="text-danger">*</span></label>
            <DateField
              name="dob"
              value={form.dob || ""}
              maxDate={new Date()}
              onChange={(v) => setForm((f) => ({ ...f, dob: v }))}
              required
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Status</label>
            <select name="enrolment_status" className="form-select" value={form.enrolment_status} onChange={onChange}>
              <option>Enrolled</option>
              <option>Pending sync</option>
              <option>Waitlisted</option>
              <option>Ended</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label">Room</label>
            <input name="room" className="form-control" value={form.room || ""} onChange={onChange} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Program</label>
            <input name="program" className="form-control" value={form.program || ""} onChange={onChange} />
          </div>
          <div className="col-12">
            <label className="form-label d-block">Days</label>
            <div className="d-flex flex-wrap gap-3 pt-1">
              {DAY_OPTIONS.map((day) => (
                <div className="form-check" key={day}>
                  <input
                    type="checkbox"
                    id={`child-day-${day}-${form.id || "new"}`}
                    className="form-check-input"
                    checked={selectedDays.includes(day)}
                    onChange={() => toggleDay(day)}
                  />
                  <label htmlFor={`child-day-${day}-${form.id || "new"}`} className="form-check-label ms-1">
                    {day}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </form>
    </AppModal>
  );
}
